import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaEllipsisV } from 'react-icons/fa';

/**
 * Componente de menú contextual reutilizable
 */
const ContextMenu = ({ 
  items = [], 
  triggerClassName = "p-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded transition-colors text-gray-400 hover:text-white",
  menuClassName = "bg-gray-700 border border-gray-600 rounded-lg shadow-xl py-2",
  itemClassName = "flex items-center px-4 py-2.5 text-sm hover:bg-gray-600 transition-colors w-full text-left",
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          triggerRef.current && !triggerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calcular posición del menú
  const calculateMenuPosition = () => {
    if (triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 200; // Ancho fijo del menú
      const padding = 8; // Padding desde los bordes de la pantalla
      
      // Calcular altura real del menú basada en el número de items
      const separatorCount = items.filter(item => item.separator).length;
      const regularItemCount = items.length - separatorCount;
      const menuHeight = (regularItemCount * 44) + (separatorCount * 9) + 16; // 44px por item, 9px por separador, 16px padding
      
      // Posición horizontal: siempre alineado a la derecha del trigger
      let left = triggerRect.right - menuWidth;
      
      // Ajustar si se sale por la izquierda
      if (left < padding) {
        left = triggerRect.left;
        // Si aún se sale por la derecha, centrar en la pantalla
        if (left + menuWidth > window.innerWidth - padding) {
          left = Math.max(padding, (window.innerWidth - menuWidth) / 2);
        }
      }
      
      // Ajustar si se sale por la derecha
      if (left + menuWidth > window.innerWidth - padding) {
        left = window.innerWidth - menuWidth - padding;
      }
      
      // Posición vertical: preferir abajo, luego arriba
      let top = triggerRect.bottom + 4;
      
      // Si se sale por abajo, mostrar arriba
      if (top + menuHeight > window.innerHeight - padding) {
        top = triggerRect.top - menuHeight - 4;
        // Si también se sale por arriba, mantener en pantalla
        if (top < padding) {
          top = padding;
        }
      }
      
      setMenuPosition({ top, left });
    }
  };

  const handleItemClick = (item) => {
    if (item.onClick && !item.disabled) {
      item.onClick();
    }
    setIsOpen(false);
  };

  const handleToggleMenu = () => {
    if (!isOpen) {
      calculateMenuPosition();
    }
    setIsOpen(!isOpen);
  };

  if (disabled || items.length === 0) {
    return null;
  }

  const menuContent = isOpen && (
    <div 
      ref={menuRef} 
      className={menuClassName}
      style={{
        position: 'fixed',
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        width: '200px',
        zIndex: 9999
      }}
    >
      {items.map((item, index) => {
        if (item.separator) {
          return <div key={index} className="border-t border-gray-600 my-1" />;
        }

        return (
          <button
            key={index}
            onClick={() => handleItemClick(item)}
            disabled={item.disabled}
            className={`${itemClassName} ${
              item.disabled 
                ? 'opacity-50 cursor-not-allowed' 
                : 'cursor-pointer'
            } ${item.className || ''}`}
            title={item.title}
          >
            {item.icon && (
              <span className={`mr-3 flex-shrink-0 ${item.iconColor || 'text-gray-400'}`}>
                {item.icon}
              </span>
            )}
            <span className={`flex-1 ${item.textColor || 'text-white'}`}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleToggleMenu}
        className={triggerClassName}
        title="Más opciones"
      >
        <FaEllipsisV size={14} />
      </button>

      {menuContent && createPortal(menuContent, document.body)}
    </>
  );
};

export default ContextMenu;